import * as THREE from 'three';

/**
 * Calculate per-vertex influence values for each segment with gradient falloff
 * Returns a map of segment index to Float32Array of influences (0-1 per vertex)
 *
 * @param {THREE.BufferGeometry} geometry - The mesh geometry
 * @param {Map<number, number>} segmentMap - Vertex index -> segment index mapping
 * @param {Float32Array} distances - Distance from boundary for each vertex
 * @param {Array<number>} segmentHeightValues - Height values per segment in mm
 * @param {number} totalSegments - Total number of segments
 * @param {Array<number>} segmentFalloffHeightValues - Falloff height values per segment in mm (optional)
 * @returns {Map<number, Float32Array>} Map of segment index to influence array
 */
export function calculateSegmentInfluences(
  geometry,
  segmentMap,
  distances,
  segmentHeightValues,
  totalSegments,
  segmentFalloffHeightValues = null
) {
  const vertexCount = geometry.attributes.position.count;
  const influences = new Map();
  const MM_TO_UNIT = 0.1;

  // Initialize influence arrays for each segment
  for (let segIdx = 0; segIdx < totalSegments; segIdx++) {
    influences.set(segIdx, new Float32Array(vertexCount));
  }

  if (!distances || !segmentHeightValues) {
    return influences;
  }

  // Fill influences from segment map using a hard cutoff at the segment bottom (including falloff),
  // and a small safety margin to prevent rendering below the total height.
  for (const [vertexIndex, segmentIndex] of segmentMap.entries()) {
    if (segmentIndex >= 0 && segmentIndex < totalSegments) {
      const influenceArray = influences.get(segmentIndex);
      if (!influenceArray) continue;

      const segmentHeightUnits = segmentHeightValues[segmentIndex] * MM_TO_UNIT;
      const falloffHeightUnits = segmentFalloffHeightValues
        ? (segmentFalloffHeightValues[segmentIndex] || 0) * MM_TO_UNIT
        : 0;
      const totalHeightUnits = segmentHeightUnits + falloffHeightUnits;

      const dist = distances ? distances[vertexIndex] : Infinity;
      if (!isFinite(totalHeightUnits) || totalHeightUnits <= 0) continue;

      // Permit only vertices strictly inside the total height (including falloff) with a small epsilon margin
      const epsilon = 1e-4;
      if (isFinite(dist) && dist < (totalHeightUnits - epsilon)) {
        influenceArray[vertexIndex] = 1.0;
      }
    }
  }

  return influences;
}

/**
 * Create a shader-based overlay mesh for visualizing a segment
 * The overlay uses supersmoothstep and distance-field rendering for ultra-smooth, blockiness-free gradients
 *
 * @param {THREE.BufferGeometry} baseGeometry - The base mesh geometry (shares position/index)
 * @param {number} segmentIndex - Index of the segment this overlay represents
 * @param {Float32Array} influences - Per-vertex influence values (0-1) - used for fallback mode
 * @param {THREE.Color} color - Segment color
 * @param {Object} config - Overlay configuration
 * @param {Float32Array} distances - Distance from boundary for each vertex (for distance-field rendering)
 * @param {number} segmentHeightMm - Height of this segment in millimeters
 * @returns {THREE.Mesh} The overlay mesh
 */
export function createSegmentOverlay(baseGeometry, segmentIndex, influences, color, config = {}, distances = null, segmentHeightMm = 0, centroidWorld = null, startPercent = 0, endPercent = 0, segmentFalloffHeightMm = 0) {
  const {
    surfaceOffset = 0.0006,
    selectedBrightness = 1.2,
    unselectedBrightness = 0.7,
    edgeSmoothness = [0.45, 0.55],
    // alphaMode: 'fill' draws the entire influence region with alpha=vInfluence
    // alphaMode: 'band' shows only a narrow edge band using smoothstep thresholds
    alphaMode = 'fill',
    useDistanceField = true,
    featherAmount = 0.15,
    influenceThreshold = 0.2,  // Only show overlay where influence > threshold (prevents full-segment bleeding)
    blendStartRatio = 0.85,     // NEW: start of blending (0..1 of height)
    maxDisplayNd = 0.95         // Hide the lowest part of band visually (0..1 of height)
  } = config;

  const MM_TO_UNIT = 0.1; // Convert mm to scene units

  // Create overlay geometry that shares position/index with base
  const overlayGeom = new THREE.BufferGeometry();

  // Share index and position
  if (baseGeometry.index) {
    overlayGeom.setIndex(baseGeometry.index);
  }
  overlayGeom.setAttribute('position', baseGeometry.attributes.position);
  overlayGeom.setAttribute('normal', baseGeometry.attributes.normal);

  // Add segment influence as attribute (for fallback mode)
  overlayGeom.setAttribute('segmentInfluence', new THREE.BufferAttribute(influences, 1));

  // Add distance from boundary as attribute (for distance-field rendering)
  if (distances) {
    overlayGeom.setAttribute('boundaryDistance', new THREE.BufferAttribute(distances, 1));
  } else {
    // Create zero-filled distance array as fallback
    const zeroDistances = new Float32Array(baseGeometry.attributes.position.count);
    overlayGeom.setAttribute('boundaryDistance', new THREE.BufferAttribute(zeroDistances, 1));
  }

  // NEW: Add segment membership mask to prevent color bleeding in concave regions
  // Mask uses actual influence values for smooth blending, not just binary 0/1
  // This creates geometry-aware boundaries that respect the 3D mesh topology
  const segmentMask = new Float32Array(influences.length);
  for (let i = 0; i < influences.length; i++) {
    // Mask is influence value (0-1), representing membership strength
    // This creates smooth boundaries in concave regions
    segmentMask[i] = Math.max(0, influences[i]);
  }
  overlayGeom.setAttribute('segmentMask', new THREE.BufferAttribute(segmentMask, 1));

  // Create shader material for smooth rendering
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: color.clone().convertSRGBToLinear() },
      uBrightness: { value: unselectedBrightness },
      uEdgeMin: { value: edgeSmoothness[0] },
      uEdgeMax: { value: edgeSmoothness[1] },
      uSurfaceOffset: { value: surfaceOffset },
      // 0 = fill, 1 = band
      uAlphaMode: { value: alphaMode === 'band' ? 1 : 0 },
      // New uniforms for Phase 1 & 2
      uSegmentHeight: { value: segmentHeightMm * MM_TO_UNIT },
      uFalloffHeight: { value: segmentFalloffHeightMm * MM_TO_UNIT },  // NEW: falloff height
      uFeatherAmount: { value: featherAmount },
      uUseDistanceField: { value: useDistanceField ? 1 : 0 },
      uInfluenceThreshold: { value: influenceThreshold },  // Threshold to limit visible area
      uBlendStart: { value: blendStartRatio },             // NEW: plateau start ratio
      // Angular clipping centroid (now in LOCAL space to match segment mapping)
      uCentroid: { value: centroidWorld ? centroidWorld.clone() : new THREE.Vector3(0,0,0) },
      uStartPercent: { value: startPercent },
      uEndPercent: { value: endPercent },
      uAngularPad: { value: 0.2 }, // percent padding to avoid tiny seams
      uMaxNd: { value: Math.max(0.0, Math.min(1.0, maxDisplayNd)) },
      // NEW: Angular blend width for smooth falloff at segment boundaries (in percent 0-100)
      uAngularBlendWidth: { value: 6.0 }  // Smooth blend over 6% of angular range (wider for more visible smoothing)
    },
    vertexShader: `
      precision highp float;

      attribute float segmentInfluence;
      attribute float boundaryDistance;
      attribute float segmentMask; // NEW: Mask to prevent color bleeding

      uniform float uSurfaceOffset;

      varying float vInfluence;
      varying float vDistance;
      varying vec3 vLocalPos; // local-space position for angular clipping
      varying float vSegmentMask; // NEW: Pass mask to fragment shader

      void main() {
        vInfluence = segmentInfluence;
        vDistance = boundaryDistance;
        vSegmentMask = segmentMask; // NEW: Pass mask value

        vLocalPos = position;

        // Do not modify position; using polygonOffset avoids silhouette changes
        vec4 worldPos = modelMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * viewMatrix * worldPos;
      }
    `,
    fragmentShader: `
      precision highp float;

      uniform vec3 uColor;
      uniform float uBrightness;
      uniform float uEdgeMin;
      uniform float uEdgeMax;
      uniform int uAlphaMode;
      uniform float uSegmentHeight;
      uniform float uFalloffHeight;
      uniform float uFeatherAmount;
      uniform int uUseDistanceField;
      uniform float uInfluenceThreshold;
      uniform float uBlendStart;
      uniform vec3 uCentroid; // in LOCAL space
      uniform float uStartPercent;
      uniform float uEndPercent;
      uniform float uAngularPad;
      uniform float uMaxNd;
      uniform float uAngularBlendWidth; // NEW: smooth blend width at segment boundaries

      varying float vInfluence;
      varying float vDistance;
      varying vec3 vLocalPos;
      varying float vSegmentMask; // NEW: Segment membership mask to prevent color bleeding

      // Supersmoothstep: 5th order polynomial for ultra-smooth gradients
      // Formula: 6t⁵ - 15t⁴ + 10t³
      // Much smoother than standard smoothstep (3rd order: 3t² - 2t³)
      // C² continuous - imperceptible transitions
      float supersmoothstep(float edge0, float edge1, float x) {
        float t = clamp((x - edge0) / (edge1 - edge0), 0.0, 1.0);
        return t * t * t * (t * (t * 6.0 - 15.0) + 10.0);
      }

      // Two-stage continuous gradient with visual separation
      // Primary zone: Strong gradient (1.0 → 0.5) - clearly visible main area
      // Separator: Thin bright stripe at boundary
      // Falloff zone: Continues gradient (0.5 → 0.15) - clearly visible secondary area
      float plateauInfluence(float distance, float blendStart) {
        float totalHeight = uSegmentHeight + uFalloffHeight;
        if (totalHeight <= 0.0) return 0.0;

        // Normalized distance relative to total height
        float nd = distance / totalHeight;

        if (uSegmentHeight <= 0.0) {
          // No primary zone, just show falloff
          return 1.0 - supersmoothstep(0.0, 1.0, nd);
        }

        // Calculate zone boundaries
        float primaryZoneEnd = uSegmentHeight / totalHeight;

        if (nd <= primaryZoneEnd) {
          // PRIMARY ZONE: Fade from 1.0 to 0.55 (stays prominent)
          float t = nd / primaryZoneEnd;
          float gradient = 1.0 - supersmoothstep(0.0, 1.0, t);
          // Map from [1.0...0.0] to [1.0...0.55]
          return 0.55 + (gradient * 0.45);
        } else {
          // FALLOFF ZONE: Fade from 0.55 to 0.35 (highly visible)
          float falloffT = (nd - primaryZoneEnd) / (1.0 - primaryZoneEnd);
          float gradient = 1.0 - supersmoothstep(0.0, 1.0, falloffT);
          // Map from [1.0...0.0] to [0.55...0.35]
          return 0.35 + (gradient * 0.20);
        }
      }

      // Convert angle degrees to percentage [0,100)
      float angleToPercent(float angleRad) {
        float deg = degrees(angleRad);
        if (deg < 0.0) deg += 360.0;
        return (deg / 360.0) * 100.0;
      }

      // Check if anglePercent is inside [start,end) with wrap-around handling
      bool inSegmentRange(float anglePercent, float startP, float endP) {
        float paddedStart = startP - uAngularPad;
        float paddedEnd = endP + uAngularPad;
        // Normalize into [0,100)
        if (paddedStart < 0.0) paddedStart += 100.0;
        if (paddedEnd >= 100.0) paddedEnd -= 100.0;

        if (paddedEnd < paddedStart) {
          // wraps: [start,100) U [0,end)
          return (anglePercent >= paddedStart) || (anglePercent < paddedEnd);
        } else {
          return (anglePercent >= paddedStart) && (anglePercent < paddedEnd);
        }
      }

      void main() {
        float influence;

        // Angular blending using LOCAL space (matches segment mapping)
        // NEW: Smooth falloff at segment boundaries instead of hard discard
        vec3 diff = vLocalPos - uCentroid;
        float angle = atan(diff.x, -diff.z);
        float anglePercent = angleToPercent(angle);

        // Calculate distance to nearest segment boundary (in angular percent space)
        float angularDistanceToBoundary;

        float paddedStart = uStartPercent - uAngularPad;
        float paddedEnd = uEndPercent + uAngularPad;
        // Normalize into [0,100)
        if (paddedStart < 0.0) paddedStart += 100.0;
        if (paddedEnd >= 100.0) paddedEnd -= 100.0;

        float distToStartBoundary, distToEndBoundary;

        if (paddedEnd < paddedStart) {
          // Wraps around 0/100: [start,100) U [0,end)
          if (anglePercent >= paddedStart || anglePercent < paddedEnd) {
            // Inside segment - calculate distance to nearest boundary
            float distToStart = anglePercent < paddedStart ? (anglePercent + 100.0 - paddedStart) : (anglePercent - paddedStart);
            float distToEnd = anglePercent >= paddedEnd ? (paddedEnd + 100.0 - anglePercent) : (paddedEnd - anglePercent);
            angularDistanceToBoundary = min(distToStart, distToEnd);
          } else {
            // Outside segment - discard with falloff
            float distOutsideStart = paddedStart - anglePercent;
            float distOutsideEnd = anglePercent - paddedEnd;
            angularDistanceToBoundary = -min(distOutsideStart, distOutsideEnd);
          }
        } else {
          // Normal range: [start,end)
          if (anglePercent >= paddedStart && anglePercent < paddedEnd) {
            // Inside segment - calculate distance to nearest boundary
            float distToStart = anglePercent - paddedStart;
            float distToEnd = paddedEnd - anglePercent;
            angularDistanceToBoundary = min(distToStart, distToEnd);
          } else {
            // Outside segment - discard with falloff
            float distOutsideStart = paddedStart - anglePercent;
            float distOutsideEnd = anglePercent - paddedEnd;
            angularDistanceToBoundary = -min(distOutsideStart, distOutsideEnd);
          }
        }

        // CHANGED: Removed angular clipping to fix concave curve visualization
        // Angular clipping (based on centroid angle) doesn't respect 3D geometry topology
        // Instead, rely on segment influence mask which is geometry-aware

        // Geometry-aware segment membership mask
        // This prevents color bleeding in concave regions by using actual influence strength
        // No hard angular boundary - just smooth blending based on geometry
        float maskFalloff = vSegmentMask;  // Direct use of influence-based mask
        if (maskFalloff < 0.01) discard;

        // Choose calculation method based on config
        if (uUseDistanceField == 1) {
          // Distance-based calculation with two-stage plateau
          float totalHeight = uSegmentHeight + uFalloffHeight;
          if (totalHeight <= 0.0) { discard; }

          // Check if beyond total height with maxNd cutoff
          float nd = vDistance / totalHeight;
          if (nd > uMaxNd) { discard; }

          // Multiply with per-vertex membership to confine overlay to segment height mask
          float membership = clamp(vInfluence, 0.0, 1.0);
          influence = plateauInfluence(vDistance, clamp(uBlendStart, 0.0, 0.999)) * membership;
        } else {
          // Phase 1: Enhanced vertex-based with supersmoothstep
          // Apply feathering to vertex influence for softer edges
          float featheredInfluence = (vInfluence - uFeatherAmount) / (1.0 - 2.0 * uFeatherAmount);
          influence = supersmoothstep(0.0, 1.0, featheredInfluence);
        }

        // Clamp to valid range
        influence = clamp(influence, 0.0, 1.0);

        // Apply threshold to limit visible area (prevents color bleeding throughout segment)
        // Only show overlay where influence > threshold
        float thresholdedInfluence = smoothstep(uInfluenceThreshold, 1.0, influence);

        // Calculate alpha based on mode
        float alpha;
        if (uAlphaMode == 0) {
          // Fill mode: show influence region above threshold
          alpha = thresholdedInfluence;
        } else {
          // Band mode: show only edge band
          alpha = supersmoothstep(uEdgeMin, uEdgeMax, thresholdedInfluence);
        }

        // CHANGED: Apply mask falloff instead of angular falloff
        // Mask falloff is based on segment membership (geometry-aware), not angular position
        alpha *= maskFalloff;

        // Add visual separator stripe at boundary between main and secondary zones
        // CHANGED: Only show stripe where mask falloff is strong (inside the segment)
        float separatorStripe = 0.0;
        if (uSegmentHeight > 0.0 && uFalloffHeight > 0.0 && maskFalloff > 0.3) {
          float totalHeight = uSegmentHeight + uFalloffHeight;
          float nd = vDistance / totalHeight;
          float primaryZoneEnd = uSegmentHeight / totalHeight;

          // Create thin bright stripe at boundary (±1% of total height)
          float stripeWidth = 0.015;
          float distFromBoundary = abs(nd - primaryZoneEnd);
          if (distFromBoundary < stripeWidth) {
            // Smooth stripe with supersmoothstep for clean edges
            separatorStripe = 1.0 - supersmoothstep(0.0, stripeWidth, distFromBoundary);
            // Boost alpha at stripe location, modulated by mask falloff
            alpha = max(alpha, separatorStripe * 0.8 * maskFalloff);
          }
        }

        // Early discard for performance
        if (alpha < 0.01) discard;

        // Apply brightness and gamma correction for proper color display
        vec3 finalColor = pow(clamp(uColor * uBrightness, 0.0, 1.0), vec3(0.5));

        // Brighten the separator stripe
        if (separatorStripe > 0.1) {
          finalColor = mix(finalColor, vec3(1.0), separatorStripe * 0.3);
        }

        gl_FragColor = vec4(finalColor, alpha * 0.85);

        // Include Three.js color space and dithering
        #include <colorspace_fragment>
        #include <dithering_fragment>
      }
    `,
    transparent: true,
    blending: THREE.NormalBlending,
    depthTest: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -0.10,
    // polygonOffsetUnits: -2.0,
  });

  // Create mesh
  const overlay = new THREE.Mesh(overlayGeom, material);
  overlay.name = `FlareSegmentOverlay_${segmentIndex}`;
  overlay.renderOrder = 500000 + segmentIndex; // High render order for proper layering
  overlay.userData.segmentIndex = segmentIndex;

  return overlay;
}

/**
 * Update overlay visual properties (color, selection state)
 *
 * @param {THREE.Mesh} overlay - The overlay mesh to update
 * @param {THREE.Color} color - New color
 * @param {boolean} isSelected - Whether this segment is selected
 * @param {Object} config - Configuration with brightness values
 * @param {number} segmentHeightMm - Height of the segment in millimeters (for distance-field rendering)
 * @param {THREE.Vector3} centroidWorld - World-space centroid for angular clipping
 * @param {number} startPercent - Start percentage for angular clipping
 * @param {number} endPercent - End percentage for angular clipping
 * @param {number} segmentFalloffHeightMm - Falloff height of the segment in millimeters
 */
export function updateOverlayVisuals(overlay, color, isSelected, config = {}, segmentHeightMm = 0, centroidWorld = null, startPercent = null, endPercent = null, segmentFalloffHeightMm = 0) {
  const {
    selectedBrightness = 1.2,
    unselectedBrightness = 0.7,
    edgeSmoothness,
    alphaMode,
    featherAmount,
    useDistanceField,
    influenceThreshold,
    blendStartRatio,
    angularBlendWidth  // NEW: angular blend width parameter
  } = config;

  if (!overlay || !overlay.material || !overlay.material.uniforms) return;

  const uniforms = overlay.material.uniforms;
  const MM_TO_UNIT = 0.1;

  // Update color (convert to linear space for proper rendering)
  uniforms.uColor.value.copy(color).convertSRGBToLinear();

  // Update brightness based on selection state
  uniforms.uBrightness.value = isSelected ? selectedBrightness : unselectedBrightness;

  // Update segment height uniform (for distance-field rendering)
  if (uniforms.uSegmentHeight && segmentHeightMm >= 0) {
    uniforms.uSegmentHeight.value = segmentHeightMm * MM_TO_UNIT;
  }

  // Update falloff height uniform
  if (uniforms.uFalloffHeight && segmentFalloffHeightMm >= 0) {
    uniforms.uFalloffHeight.value = segmentFalloffHeightMm * MM_TO_UNIT;
  }

  // Update feathering amount
  if (typeof featherAmount !== 'undefined' && uniforms.uFeatherAmount) {
    uniforms.uFeatherAmount.value = featherAmount;
  }

  // Update distance field mode
  if (typeof useDistanceField !== 'undefined' && uniforms.uUseDistanceField) {
    uniforms.uUseDistanceField.value = useDistanceField ? 1 : 0;
  }

  // Update influence threshold
  if (typeof influenceThreshold !== 'undefined' && uniforms.uInfluenceThreshold) {
    uniforms.uInfluenceThreshold.value = influenceThreshold;
  }

  // Update blend start ratio (plateau start)
  if (typeof blendStartRatio !== 'undefined' && uniforms.uBlendStart) {
    uniforms.uBlendStart.value = Math.max(0, Math.min(1, blendStartRatio));
  }

  // Optionally update edge thresholds if provided
  if (edgeSmoothness && edgeSmoothness.length === 2) {
    if (uniforms.uEdgeMin) uniforms.uEdgeMin.value = edgeSmoothness[0];
    if (uniforms.uEdgeMax) uniforms.uEdgeMax.value = edgeSmoothness[1];
  }

  // Optionally update alpha mode
  if (typeof alphaMode !== 'undefined' && uniforms.uAlphaMode) {
    uniforms.uAlphaMode.value = alphaMode === 'band' ? 1 : 0;
  }

  // Update centroid and segment bounds if provided
  if (centroidWorld && uniforms.uCentroid) {
    uniforms.uCentroid.value.copy(centroidWorld);
  }
  if (startPercent !== null && uniforms.uStartPercent) {
    uniforms.uStartPercent.value = startPercent;
  }
  if (endPercent !== null && uniforms.uEndPercent) {
    uniforms.uEndPercent.value = endPercent;
  }

  // NEW: Update angular blend width for smooth boundary transitions
  if (typeof angularBlendWidth !== 'undefined' && uniforms.uAngularBlendWidth) {
    uniforms.uAngularBlendWidth.value = Math.max(0.5, Math.min(15.0, angularBlendWidth));
  }
}

/**
 * Update the segment influence values on an existing overlay
 *
 * @param {THREE.Mesh} overlay - The overlay mesh to update
 * @param {Float32Array} newInfluences - New influence values
 */
export function updateOverlayInfluences(overlay, newInfluences) {
  if (!overlay || !overlay.geometry) return;

  const influenceAttr = overlay.geometry.getAttribute('segmentInfluence');
  if (!influenceAttr) return;

  influenceAttr.array.set(newInfluences);
  influenceAttr.needsUpdate = true;
}

/**
 * Update the distance field values on an existing overlay
 * Used for distance-based fragment shader calculations
 *
 * @param {THREE.Mesh} overlay - The overlay mesh to update
 * @param {Float32Array} distances - Distance from boundary for each vertex
 */
export function updateOverlayDistances(overlay, distances) {
  if (!overlay || !overlay.geometry || !distances) return;

  const distanceAttr = overlay.geometry.getAttribute('boundaryDistance');
  if (!distanceAttr) return;

  distanceAttr.array.set(distances);
  distanceAttr.needsUpdate = true;
}

/**
 * Create a segment-colored overlay mesh using the actual segment map from geometry
 * This approach ensures visualization matches the actual geometry deformation exactly
 * NEW: Single mesh with per-vertex segment coloring instead of multiple overlays
 *
 * @param {THREE.BufferGeometry} baseGeometry - The base geometry
 * @param {Map} segmentMap - Vertex index -> segment index (from geometry deformation)
 * @param {Array} segmentNames - Segment names ['purple', 'yellow', etc.]
 * @param {Object} config - Configuration object
 * @returns {THREE.Mesh} Single colored overlay mesh
 */
export function createSegmentColoredOverlay(baseGeometry, segmentMap, segmentNames, config = {}) {
  const {
    surfaceOffset = 0.0006,
    selectedBrightness = 1.2,
    unselectedBrightness = 0.7,
    selectedSegmentIndex = -1
  } = config;

  // Segment colors
  const SEGMENT_COLORS = {
    purple: new THREE.Color('#B984D9'),
    yellow: new THREE.Color('#FFD152'),
    orange: new THREE.Color('#F19B4A'),
    blue: new THREE.Color('#3B9FF5'),
    violet: new THREE.Color('#9093DF')
  };

  // Create geometry that shares position/index/normal
  const overlayGeom = new THREE.BufferGeometry();
  if (baseGeometry.index) {
    overlayGeom.setIndex(baseGeometry.index);
  }
  overlayGeom.setAttribute('position', baseGeometry.attributes.position);
  overlayGeom.setAttribute('normal', baseGeometry.attributes.normal);

  // Create color attribute based on segment map WITH boundary blending
  const vertexCount = baseGeometry.attributes.position.count;
  const colors = new Float32Array(vertexCount * 3);
  const defaultColor = new THREE.Color(0x808080);

  // First, assign base colors to segment vertices
  for (let i = 0; i < vertexCount; i++) {
    const segmentIndex = segmentMap.get(i);
    let color = defaultColor;

    if (segmentIndex !== undefined && segmentIndex >= 0 && segmentIndex < segmentNames.length) {
      const segmentName = segmentNames[segmentIndex];
      color = SEGMENT_COLORS[segmentName] || defaultColor;

      // Brighten selected segment
      if (segmentIndex === selectedSegmentIndex) {
        color = color.clone().multiplyScalar(selectedBrightness / unselectedBrightness);
      }
    }

    colors[i * 3] = color.r;
    colors[i * 3 + 1] = color.g;
    colors[i * 3 + 2] = color.b;
  }

  // NEW: Blend colors at segment boundaries for smooth transitions
  if (baseGeometry.index) {
    const indices = baseGeometry.index.array;

    // Find boundary vertices (vertices with neighbors in different segments)
    const boundaryVertices = new Set();
    for (let i = 0; i < indices.length; i += 3) {
      const a = indices[i], b = indices[i+1], c = indices[i+2];
      const segA = segmentMap.get(a), segB = segmentMap.get(b), segC = segmentMap.get(c);

      // If triangle has vertices from different segments, they're on boundary
      if (segA !== segB || segB !== segC || segA !== segC) {
        boundaryVertices.add(a);
        boundaryVertices.add(b);
        boundaryVertices.add(c);
      }
    }

    // For boundary vertices, blend colors toward neutral gray
    // This creates smooth color transitions instead of sharp boundaries
    for (const vertexIdx of boundaryVertices) {
      if (segmentMap.has(vertexIdx)) {
        // Blend the vertex color toward gray (neutral) for softer transition
        const originalR = colors[vertexIdx * 3];
        const originalG = colors[vertexIdx * 3 + 1];
        const originalB = colors[vertexIdx * 3 + 2];

        // Blend 50% toward gray (0.5, 0.5, 0.5) at boundaries
        colors[vertexIdx * 3] = originalR * 0.7 + 0.5 * 0.3;
        colors[vertexIdx * 3 + 1] = originalG * 0.7 + 0.5 * 0.3;
        colors[vertexIdx * 3 + 2] = originalB * 0.7 + 0.5 * 0.3;
      }
    }
  }

  overlayGeom.setAttribute('color', new THREE.BufferAttribute(colors, 3));

  // NEW: Create opacity attribute for smooth boundary transitions
  // Vertices in segment map get full opacity, neighbors get smooth falloff
  // This creates a natural fade zone at flare boundaries instead of sharp edges
  const opacities = new Float32Array(vertexCount);

  // First pass: mark vertices in segment map as 1.0
  for (let i = 0; i < vertexCount; i++) {
    opacities[i] = segmentMap.has(i) ? 1.0 : 0.0;
  }

  // Second pass: smooth the boundary by averaging with neighbors (1-2 iterations)
  if (baseGeometry.index) {
    const indices = baseGeometry.index.array;
    const smoothOpacities = new Float32Array(vertexCount);
    smoothOpacities.set(opacities);

    // Build adjacency map
    const adjacency = new Map();
    for (let i = 0; i < indices.length; i += 3) {
      const a = indices[i], b = indices[i+1], c = indices[i+2];
      [a, b, c].forEach(v => {
        if (!adjacency.has(v)) adjacency.set(v, new Set());
      });
      adjacency.get(a).add(b); adjacency.get(a).add(c);
      adjacency.get(b).add(a); adjacency.get(b).add(c);
      adjacency.get(c).add(a); adjacency.get(c).add(b);
    }

    // Smooth opacity at boundaries by interpolating with neighbors
    for (let i = 0; i < vertexCount; i++) {
      if (opacities[i] === 0 && adjacency.has(i)) {
        // Vertex outside segment: average with neighbors that are inside
        let neighborSum = 0, count = 0;
        for (const neighbor of adjacency.get(i)) {
          if (opacities[neighbor] > 0) {
            neighborSum += opacities[neighbor];
            count++;
          }
        }
        if (count > 0) {
          smoothOpacities[i] = neighborSum / count * 0.7; // 70% of neighbor opacity for soft edge
        }
      }
    }
    opacities.set(smoothOpacities);
  }

  overlayGeom.setAttribute('opacity', new THREE.BufferAttribute(opacities, 1));

  // Simple material that uses vertex colors and opacity
  const material = new THREE.MeshStandardMaterial({
    vertexColors: true,
    transparent: true,
    opacity: 0.75,  // Reduced from 0.85 for softer blending
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -0.10,
    depthWrite: false
  });

  const overlay = new THREE.Mesh(overlayGeom, material);
  overlay.name = 'SegmentColoredOverlay';
  overlay.renderOrder = 500000;
  overlay.userData.isSegmentOverlay = true;

  return overlay;
}

/**
 * Update segment colors on an existing colored overlay
 *
 * @param {THREE.Mesh} overlay - The overlay mesh
 * @param {Map} segmentMap - Vertex index -> segment index
 * @param {Array} segmentNames - Segment names
 * @param {number} selectedSegmentIndex - Currently selected segment
 */
export function updateSegmentColoredOverlay(overlay, segmentMap, segmentNames, selectedSegmentIndex = -1) {
  if (!overlay || !overlay.geometry) return;

  const SEGMENT_COLORS = {
    purple: new THREE.Color('#B984D9'),
    yellow: new THREE.Color('#FFD152'),
    orange: new THREE.Color('#F19B4A'),
    blue: new THREE.Color('#3B9FF5'),
    violet: new THREE.Color('#9093DF')
  };

  const colorAttr = overlay.geometry.getAttribute('color');
  if (!colorAttr) return;

  const colors = colorAttr.array;
  const defaultColor = new THREE.Color(0x808080);
  const vertexCount = colorAttr.count;

  for (let i = 0; i < vertexCount; i++) {
    const segmentIndex = segmentMap.get(i);
    let color = defaultColor;

    if (segmentIndex !== undefined && segmentIndex >= 0 && segmentIndex < segmentNames.length) {
      const segmentName = segmentNames[segmentIndex];
      color = SEGMENT_COLORS[segmentName] || defaultColor;

      if (segmentIndex === selectedSegmentIndex) {
        color = color.clone().multiplyScalar(1.3);
      }
    }

    colors[i * 3] = color.r;
    colors[i * 3 + 1] = color.g;
    colors[i * 3 + 2] = color.b;
  }

  colorAttr.needsUpdate = true;
}

/**
 * Dispose an overlay mesh and its resources
 *
 * @param {THREE.Mesh} overlay - The overlay mesh to dispose
 */
export function disposeOverlay(overlay) {
  if (!overlay) return;

  // Remove from parent
  if (overlay.parent) {
    overlay.parent.remove(overlay);
  }

  // Dispose geometry (but not shared attributes - they're references)
  // Only dispose the influence attribute which is unique to this overlay
  const influenceAttr = overlay.geometry?.getAttribute('segmentInfluence');
  if (influenceAttr) {
    // BufferAttributes don't have a dispose method, just let GC handle it
    overlay.geometry.deleteAttribute('segmentInfluence');
  }

  overlay.geometry?.dispose();

  // Dispose material
  overlay.material?.dispose();
}

/**
 * Dispose all overlays in a map
 *
 * @param {Map<number, THREE.Mesh>} overlayMap - Map of segment index to overlay mesh
 */
export function disposeAllOverlays(overlayMap) {
  if (!overlayMap) return;

  for (const overlay of overlayMap.values()) {
    disposeOverlay(overlay);
  }

  overlayMap.clear();
}
